import { useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { LayoutGrid, Building2, MessagesSquare, Settings, ScrollText, User, LogOut, Menu, Images, Users, Sparkles, Mail, FolderOpen } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Logo } from "@/components/common/Logo";
import { useAuth } from "@/context/AuthContext";

const ITEMS = [
  { to: "/admin", label: "Cruscotto", icon: LayoutGrid, end: true, id: "dashboard" },
  { to: "/admin/centri", label: "Centri", icon: Building2, id: "centers" },
  { to: "/admin/ticket", label: "Ticket", icon: MessagesSquare, id: "tickets" },
  { to: "/admin/social", label: "Contenuti social", icon: Images, id: "social" },
  { to: "/admin/documenti", label: "Documenti per tutti", icon: FolderOpen, id: "shared-docs" },
  { to: "/admin/novita", label: "Novità", icon: Sparkles, id: "news" },
  { to: "/admin/email", label: "Email automatiche", icon: Mail, id: "emails" },
  { to: "/admin/staff", label: "Staff", icon: Users, superOnly: true, id: "staff" },
  { to: "/admin/impostazioni", label: "Impostazioni", icon: Settings, superOnly: true, id: "settings" },
  { to: "/admin/registro", label: "Registro attività", icon: ScrollText, superOnly: true, id: "audit" },
  { to: "/admin/account", label: "Il mio account", icon: User, id: "account" },
];

function NavItems({ onNavigate }) {
  const { user, logout } = useAuth();
  return (
    <div className="flex flex-col h-full">
      <div className="px-7 pt-8 pb-10">
        <Logo size="h-5" />
        <p className="mt-5 inline-flex items-center gap-2 rounded-full border border-gold/30 bg-gold/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-gold">
          <span className="w-1.5 h-1.5 rounded-full bg-gold shadow-[0_0_8px_rgba(212,175,55,0.9)]" />
          {user?.role === "superadmin" ? "Super Admin" : "Admin"}
        </p>
      </div>
      <nav className="flex-1 px-4 space-y-1 stagger overflow-y-auto">
        {ITEMS.filter((i) => !i.superOnly || user?.role === "superadmin").map((i) => (
          <NavLink key={i.to} to={i.to} end={i.end} onClick={onNavigate} data-testid={`admin-nav-${i.id}`}
            className={({ isActive }) => `group relative flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-[background-color,color,transform] duration-300 hover:translate-x-0.5 ${isActive ? "bg-gradient-to-r from-gold/20 to-gold/[0.02] text-white shadow-[inset_2px_0_0_#D4AF37]" : "text-warm-text hover:text-white hover:bg-white/[0.04]"}`}>
            <i.icon className="w-4 h-4 text-gold/80 group-hover:text-gold transition-colors" strokeWidth={1.5} /> {i.label}
          </NavLink>
        ))}
      </nav>
      <div className="px-7 py-6 border-t border-gold/15">
        <p className="text-sm truncate">{user?.name}</p>
        <p className="text-xs text-warm-text truncate">{user?.email}</p>
        <button data-testid="admin-logout-btn" onClick={logout} className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-warm-text hover:text-gold transition-colors">
          <LogOut className="w-3.5 h-3.5" /> Esci
        </button>
      </div>
    </div>
  );
}

export default function AdminLayout({ children }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  return (
    <div className="min-h-screen lg:flex">
      <aside className="hidden lg:block w-64 shrink-0 bg-[#0D0D0F]/90 backdrop-blur-xl border-r border-gold/15 sticky top-0 h-screen">
        <NavItems />
      </aside>
      <header className="lg:hidden sticky top-0 z-30 glass border-b border-gold/15 h-16 px-5 flex items-center justify-between">
        <Link to="/admin"><Logo size="h-5" /></Link>
        <button data-testid="admin-mobile-menu-btn" onClick={() => setOpen(true)} aria-label="Menu" className="w-10 h-10 rounded-full border border-gold/40 bg-gold/10 flex items-center justify-center text-gold active:scale-95 transition-transform"><Menu className="w-5 h-5" strokeWidth={1.4} /></button>
      </header>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="p-0 w-72 bg-[#0D0D0F] border-r border-gold/20">
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <NavItems onNavigate={() => setOpen(false)} />
        </SheetContent>
      </Sheet>
      <main key={pathname} className="flex-1 min-w-0 px-5 sm:px-8 lg:px-12 py-8 lg:py-12 animate-page-in"><div className="max-w-7xl mx-auto">{children}</div></main>
    </div>
  );
}
